// src/features/home/components/MainContent.tsx

import React, { useState } from 'react';
import styles from './MainContent.module.scss';
import { useTranslation, Trans } from 'react-i18next';
import ExperienceCard from './ExperienceCard';
import ProjectCard from './ProjectCard';
import { NavLink, useNavigate } from 'react-router-dom';
import ArticleCard from './ArticleCard';
import Footer from './Footer';
import alvoImage from '../../../shared/assets/images/project-thumbnails/alvo.png';
import lasemaineImage from '../../../shared/assets/images/project-thumbnails/lasemaine.png';
import amurabiImage from '../../../shared/assets/images/project-thumbnails/amurabi.png';
import tunisrhImage from '../../../shared/assets/images/project-thumbnails/tunisrh.png';
import bookmyroomImage from '../../../shared/assets/images/project-thumbnails/bookmyroom.jpg';
import quoteFlowImage from '../../../shared/assets/images/project-thumbnails/Flitter-quote.png';
import { motion, useReducedMotion, AnimatePresence } from 'framer-motion';
import { useMediaQuery } from 'react-responsive';
import { Projects } from '../../projects/types/project';

interface MainContentProps {
  aboutRef: React.RefObject<HTMLDivElement>;
  experienceRef: React.RefObject<HTMLDivElement>;
  projectsRef: React.RefObject<HTMLDivElement>;
}

const ExternalLinkIcon = () => (
  <svg
    className={styles.externalLinkIcon}
    xmlns="http://www.w3.org/2000/svg"
    fill="currentColor"
    viewBox="0 0 20 20"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      d="M5.22 14.78a.75.75 0 001.06 0l7.22-7.22v5.69a.75.75 0 001.5 0v-7.5A.75.75 0 0013.25 5h-7.5a.75.75 0 000 1.5h5.69L4.22 13.72a.75.75 0 000 1.06z"
      clipRule="evenodd"
    />
  </svg>
);

const ExternalLink: React.FC<{ href: string; children?: React.ReactNode }> = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className={styles.link}>
    {children}
  </a>
);

const MainContent: React.FC<MainContentProps> = ({
  aboutRef,
  experienceRef,
  projectsRef,
}) => {
  const { t, i18n } = useTranslation(['translation', 'projects']);
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();

  // Get experiences and articles from translation files
  const experiences = t('experience.experiences', { returnObjects: true }) as any[];
  const articles = t('articles', { returnObjects: true }) as any[];

  // Get actual projects from projects.json
  const projectsData = i18n.getResourceBundle(i18n.language, 'projects') as Projects;
  
  // Explicitly type aboutDescription as string[]
  const aboutDescription = t('about.description', { returnObjects: true }) as string[];

  // Image mapping for local thumbnails
  const imageMap: { [key: string]: string } = {
    'la-semaine': lasemaineImage,
    'alvo-marketplace': alvoImage,
    'amurabi-poc': amurabiImage,
    'tunisrh': tunisrhImage,
    'bookmyroom': bookmyroomImage,
    'quote-flow': quoteFlowImage,
  };

  // Get the first 2 projects from projects.json and format them for the home page
  const getLatestProjects = () => {
    if (!projectsData?.projects) return [];
    
    return projectsData.projects.slice(0, 2).map((project) => ({
      id: project.id,
      title: project.hero.title,
      description: project.hero.subtitle,
      image: imageMap[project.id] || project.hero.image,
      technologies: project.technologies.map((tech) => tech.name),
      // Don't include projectUrl so it navigates to detail page instead
    }));
  };

  const latestProjects = getLatestProjects();

  // Handle project card click to navigate to detail page
  const handleProjectClick = (projectId: string) => {
    navigate(`/projects/${projectId}`);
  };

  // Animation variants for sections
  const sectionVariants = {
    hidden: prefersReducedMotion ? {} : { opacity: 0, y: 30 },
    visible: prefersReducedMotion
      ? {}
      : { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
  };

  const handleDownload = (language: 'en' | 'fr') => {
    const pdfUrl = language === 'en'
      ? '/Jamil Joo – Full-Stack Software Engineer (Web & Mobile).pdf'
      : '/Jamil Joo – Ingénieur Logiciel Full-Stack (Web & Mobile).pdf';
    
    // Open PDF in new tab for viewing
    window.open(pdfUrl, '_blank');
  };

  return (
    <main className={styles.mainContent}>
      {/* About Section */}
      <motion.section
        id="about"
        ref={aboutRef}
        className={`${styles.section} ${styles.aboutSection}`}
        initial="hidden"
        whileInView="visible"
        variants={sectionVariants}
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className={styles.spacer}></div>
        {aboutDescription.map((paragraph: string, index: number) => (
          <p key={index}>
            <Trans
              i18nKey={`about.description.${index}`}
              components={{
                linkedin: <ExternalLink href="https://www.linkedin.com/in/jamiljoo/" />,
                company: <ExternalLink href="https://justcosa.com" />,
                react: <ExternalLink href="https://reactjs.org" />,
                rn: <ExternalLink href="https://reactnative.dev" />,
                insta: <ExternalLink href="https://www.instagram.com/jamil_joo" />,
                upwork: <ExternalLink href="https://www.upwork.com/freelancers/~011243a6d19af75131" />,
                strong: <strong />,
              }}
            />
          </p>
        ))}
      </motion.section>

      {/* Experience Section */}
      <motion.section
        id="experience"
        ref={experienceRef}
        className={`${styles.section} ${styles.experienceSection}`}
        initial="hidden"
        whileInView="visible"
        variants={sectionVariants}
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className={styles.experienceListContainer}>
          <ol className={styles.experienceList}>
            {experiences.map((exp, index) => (
              <ExperienceCard key={index} {...exp} />
            ))}
          </ol>
        </div>
        <div className={styles.resumeLinkWrapper}>
          <div className={styles.resumeCard}>
            <div className={styles.hoverOverlay}></div>
            <div className={styles.resumeLabel}>Resume</div>
            <div className={styles.resumeOptions}>
              <div className={styles.resumeOptionsContainer}>
                <a
                  onClick={() => handleDownload('en')}
                  className={styles.resumeOptionLink}
                  style={{ cursor: 'pointer' }}
                >
                  <svg
                    className={styles.downloadIcon}
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                    />
                  </svg>
                  {t('experience.englishResumeTitle')}
                  <span className={styles.languageBadge}>EN</span>
                </a>

                <a
                  onClick={() => handleDownload('fr')}
                  className={styles.resumeOptionLink}
                  style={{ cursor: 'pointer' }}
                >
                  <svg
                    className={styles.downloadIcon}
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                    />
                  </svg>
                  {t('experience.frenchResumeTitle')}
                  <span className={styles.languageBadge}>FR</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Projects Section */}
      <motion.section
        id="projects"
        ref={projectsRef}
        className={`${styles.section} ${styles.projectsSection}`}
        initial="hidden"
        whileInView="visible"
        variants={sectionVariants}
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className={styles.spacer}></div>
        <ul className={styles.projectList}>
          {latestProjects.map((proj, index) => (
            <div
              key={proj.id}
              onClick={() => handleProjectClick(proj.id)}
              style={{ cursor: 'pointer' }}
            >
              <ProjectCard {...proj} />
            </div>
          ))}
        </ul>
        <div className={styles.buttonContainer}>
          <NavLink to="/projects" className={styles.linkButton}>
            <span>
              {t('homeProjects.seeMoreProjects')}
              <ExternalLinkIcon />
            </span>
          </NavLink>
        </div>
      </motion.section>

      {/* Articles Section */}
      <motion.section
        className={`${styles.section} ${styles.articlesSection}`}
        initial="hidden"
        whileInView="visible"
        variants={sectionVariants}
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className={styles.spacer}></div>
        <ul className={styles.articleList}>
          {articles.map((article, index) => (
            <ArticleCard key={index} {...article} />
          ))}
        </ul>
      </motion.section>
      {/* Footer */}
      <Footer />
    </main>
  );
};

export default MainContent;